import { useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField, Typography } from '@mui/material';
import CatchRegistrationCatch from '../../../Home/Components/CatchRegistrationModal/CatchRegistrationCatch/CatchRegistrationCatch';
import PostCatchBody from '../../../../Models/CatchRegistrationModels/PostCatchBody';
import { NoteDetail, ReweightingReferences, WeightLineInput } from '../../../../Models/ReWeightingModels/Workspace';
import { reweightingError } from '../../../../Services/ReweightingWorkspaceService';

interface Props {
  detail: NoteDetail;
  references: ReweightingReferences;
  line?: WeightLineInput;
  saving: boolean;
  reloadRequired: boolean;
  onClose: () => void;
  onSave: (line: WeightLineInput) => Promise<void>;
}
const parseNumber = (value: string) => value.trim() === '' ? null : Number(value.replace(',', '.'));

export default function WeightLineDialog({ detail, references, line, saving, reloadRequired, onClose, onSave }: Props) {
  const stockInput = useRef<HTMLInputElement>(null);
  const quantityInput = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<PostCatchBody>(() => line ? ({
    fishStockId: line.fishStockId, fishingAreaId: line.fishingAreaId,
    conditionId: line.conditionId, storageMethodId: line.storageMethodId, destinyId: line.destinyId,
    isUnderKilogram: line.isUnderKilogram,
  }) : {});
  const [quantity, setQuantity] = useState(line?.quantity.toString().replace('.', ',') ?? '');
  const [ungutted, setUngutted] = useState(line?.unguttedQuantity?.toString() ?? '');
  const [ice, setIce] = useState(line?.declaredIcePercentage?.toString().replace('.', ',') ?? '');
  const [comment, setComment] = useState(line?.comment ?? '');
  const [error, setError] = useState('');
  const save = async () => {
    if (saving || reloadRequired) return;
    setError('');
    const amount = parseNumber(quantity);
    const unguttedAmount = parseNumber(ungutted);
    const icePercentage = parseNumber(ice);
    if (!form.fishStockId || !form.fishingAreaId || !form.conditionId || !form.storageMethodId || !form.destinyId) {
      setError('Veldu fisktegund, ástand, veiðisvæði, geymslu og afdrif.'); return;
    }
    if (amount === null || !Number.isFinite(amount) || amount < 0 || amount > 99999999.99 || !/^\d+([.,]\d{1,2})?$/.test(quantity.trim())) {
      setError('Skráðu magn í kílóum, með allt að tveimur aukastöfum.'); return;
    }
    if (unguttedAmount !== null && (!Number.isInteger(unguttedAmount) || unguttedAmount < 0 || unguttedAmount > 99999999)) {
      setError('Óslægt magn þarf að vera í heilum kílóum.'); return;
    }
    if (icePercentage !== null && (!Number.isFinite(icePercentage) || icePercentage < 0 || icePercentage > 99.99 || !/^\d+([.,]\d{1,2})?$/.test(ice.trim()))) {
      setError('Ísprósenta þarf að vera á bilinu 0–99,99.'); return;
    }
    const value: WeightLineInput = {
      id: line?.id ?? null, fishStockId: form.fishStockId, fishingAreaId: form.fishingAreaId,
      conditionId: form.conditionId, storageMethodId: form.storageMethodId, destinyId: form.destinyId,
      isUnderKilogram: !!form.isUnderKilogram, quantity: amount, unguttedQuantity: unguttedAmount,
      declaredIcePercentage: icePercentage, comment: comment.trim() || null,
    };
    if (!references.combinations.some(c => c.fishStockId === value.fishStockId && c.fishingAreaId === value.fishingAreaId &&
      c.conditionId === value.conditionId && c.storageMethodId === value.storageMethodId && c.destinyId === value.destinyId &&
      c.isUnderKilogram === value.isUnderKilogram)) {
      setError('Þessi samsetning aflaupplýsinga er ekki leyfð.'); return;
    }
    try { await onSave(value); }
    catch (value) { setError(reweightingError(value)); }
  };
  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="md" aria-labelledby="weight-line-title"
      PaperProps={{ sx: { maxWidth: 760 } }} TransitionProps={{ onEntered: () => {
        const input = line ? quantityInput.current : stockInput.current;
        input?.focus();
        if (line) input?.select();
      } }}>
      <Box component="form" onSubmit={event => { event.preventDefault(); void save(); }} aria-busy={saving}>
        <DialogTitle id="weight-line-title" sx={{ px: 3, pt: 2.5, pb: 2, fontSize: 22, fontWeight: 700 }}>
          {line ? 'Breyta vigtarlínu' : 'Ný vigtarlína'}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            <Box component="span" fontWeight={700}>{detail.note.shipNumber}</Box> {detail.note.shipName} · Nóta #{detail.note.id}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}{reloadRequired && ' Lokaðu glugganum og sæktu nótuna aftur.'}</Alert>}
          <Box sx={{ pt: 1 }}>
            <CatchRegistrationCatch
              layout="compact" stockInputRef={stockInput}
              handleFormChange={change => setForm(previous => ({ ...previous, ...change }))}
              formData={form} allowedCombinations={references.combinations} weight={undefined}
              selectedWeightNote={{ isClosed: false, weightNoteType: { id: 22, name: 'Frá vigtunarleyfishafa' },
                gear: { id: detail.note.gearId, name: detail.note.gearName, forbiddenStocks: [] } }}
              fishAreas={references.fishingAreas} storageMethods={references.storageMethods}
              destinies={references.destinies} fishingStocks={references.fishingStocks} conditions={references.conditions}
              disabled={saving || reloadRequired} containsExported={false}
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1.25fr 1fr 1fr' }, gap: 2, alignItems: 'center',
            mt: 2.5, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
            <TextField inputRef={quantityInput} fullWidth required label="Nettóafli" value={quantity}
              onFocus={event => event.target.select()} disabled={saving || reloadRequired}
              onChange={event => setQuantity(event.target.value)} inputProps={{ inputMode: 'decimal' }}
              InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
              sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                '& input': { fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums', py: 1.5 } }} />
            <TextField fullWidth label="Óslægt magn" value={ungutted} onChange={event => setUngutted(event.target.value)}
              disabled={saving || reloadRequired} placeholder="Óskráð"
              inputProps={{ inputMode: 'numeric' }} InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
            />
            <TextField fullWidth label="Ísprósenta" value={ice} onChange={event => setIce(event.target.value)}
              disabled={saving || reloadRequired} placeholder="Óskráð"
              inputProps={{ inputMode: 'decimal' }} InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            />
          </Box>
          <TextField fullWidth multiline minRows={2} label="Athugasemd við línu" value={comment}
            disabled={saving || reloadRequired} onChange={event => setComment(event.target.value)} inputProps={{ maxLength: 400 }} sx={{ mt: 2.5 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button disabled={saving} onClick={onClose}>Hætta við</Button>
          <Button variant="contained" type="submit" disabled={saving || reloadRequired}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {saving ? 'Vista…' : line ? 'Vista línu' : 'Skrá línu'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
